import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EditIcon from '@mui/icons-material/Edit';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Fab from '@mui/material/Fab';
import { competitionConfigsService } from '../services/supabase';

const RevisionCompareDialog = ({ open, onClose, competitionId }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState(null);
  const [leftRevision, setLeftRevision] = useState('');
  const [rightRevision, setRightRevision] = useState('');
  const [leftContent, setLeftContent] = useState(null);
  const [rightContent, setRightContent] = useState(null);
  const [diff, setDiff] = useState(null);
  const [lastCompared, setLastCompared] = useState({ left: null, right: null });
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showDebugFields, setShowDebugFields] = useState(false);
  const [debugExpanded, setDebugExpanded] = useState({});
  const [accordionExpanded, setAccordionExpanded] = useState({});
  const contentRef = React.useRef(null);

  useEffect(() => {
    if (open && competitionId) {
      loadVersions();
    }
  }, [open, competitionId]);

  // Auto-compare when both revisions are selected and they differ from last comparison
  useEffect(() => {
    if (leftRevision && rightRevision && leftRevision !== rightRevision) {
      if (lastCompared.left !== leftRevision || lastCompared.right !== rightRevision) {
        handleCompare();
      }
    }
  }, [leftRevision, rightRevision]);

  // Re-calculate diff when debug toggle changes
  useEffect(() => {
    if (leftContent && rightContent) {
      const differences = calculateDifferences(leftContent, rightContent);
      setDiff(differences);
    }
  }, [showDebugFields]);

  // Track scroll position for scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const scrollTop = contentRef.current.scrollTop;
        setShowScrollTop(scrollTop > 300);
      }
    };

    const content = contentRef.current;
    if (content) {
      content.addEventListener('scroll', handleScroll);
      return () => content.removeEventListener('scroll', handleScroll);
    }
  }, [open]);

  const scrollToTop = () => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadVersions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await competitionConfigsService.getVersions(competitionId);
      setVersions(data);
      
      // Auto-select latest and previous revision if available
      if (data.length >= 2) {
        setLeftRevision(data[1].revision);  // Older version as base
        setRightRevision(data[0].revision);  // Newer version to compare to
      }
    } catch (err) {
      setError(err.message || 'Failed to load versions');
      console.error('Error loading versions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!leftRevision || !rightRevision) {
      setError('Please select two revisions to compare');
      return;
    }

    try {
      setComparing(true);
      setError(null);
      const startTime = Date.now();

      const [left, right] = await Promise.all([
        competitionConfigsService.getVersion(competitionId, leftRevision),
        competitionConfigsService.getVersion(competitionId, rightRevision)
      ]);

      setLeftContent(left?.content);
      setRightContent(right?.content);

      // Calculate differences
      const differences = calculateDifferences(left?.content, right?.content);
      
      // Ensure loading state shows for at least 400ms for smooth UX
      const elapsed = Date.now() - startTime;
      const minDelay = 400;
      if (elapsed < minDelay) {
        await new Promise(resolve => setTimeout(resolve, minDelay - elapsed));
      }
      
      setDiff(differences);
      setLastCompared({ left: leftRevision, right: rightRevision });
    } catch (err) {
      setError(err.message || 'Failed to compare revisions');
      console.error('Error comparing revisions:', err);
    } finally {
      setComparing(false);
    }
  };

  const calculateDifferences = (left, right) => {
    const differences = {
      competition: [],
      gymnasts: { added: [], removed: [], modified: [] },
      teams: { added: [], removed: [], modified: [] },
      sessions: { added: [], removed: [], modified: [] },
      judges: { added: [], removed: [], modified: [] },
      coaches: { added: [], removed: [], modified: [] },
      statistics: [],
      other: []
    };

    // Competition metadata changes
    if (left?.competition && right?.competition) {
      Object.keys(right.competition).forEach(key => {
        if (JSON.stringify(left.competition[key]) !== JSON.stringify(right.competition[key])) {
          differences.competition.push({
            field: key,
            old: left.competition[key],
            new: right.competition[key]
          });
        }
      });
    }

    // Array-based comparisons
    const compareArrays = (leftArr = [], rightArr = [], key) => {
      // For sessions without IDs, create a composite key
      const getItemKey = (item) => {
        if (item.id) return item.id;
        // Fallback composite key for sessions - use sessionNumber as primary identifier
        if (key === 'sessions') {
          // sessionNumber should be the most reliable unique identifier
          if (item.sessionNumber !== undefined && item.sessionNumber !== null) {
            return `session-${item.sessionNumber}`;
          }
          // Fallback to name/date/time if no sessionNumber
          return `${item.sessionName || 'unnamed'}-${item.date || 'nodate'}-${item.startTime || 'notime'}`;
        }
        // Use JSON as last resort
        return JSON.stringify(item);
      };

      const leftIds = new Map(leftArr.map(item => [getItemKey(item), item]));
      const rightIds = new Map(rightArr.map(item => [getItemKey(item), item]));

      // Added items
      rightArr.forEach(item => {
        if (!leftIds.has(getItemKey(item))) {
          differences[key].added.push(item);
        }
      });

      // Removed items
      leftArr.forEach(item => {
        if (!rightIds.has(getItemKey(item))) {
          differences[key].removed.push(item);
        }
      });

      // Modified items
      leftArr.forEach(leftItem => {
        const itemKey = getItemKey(leftItem);
        const rightItem = rightIds.get(itemKey);
        if (rightItem && JSON.stringify(leftItem) !== JSON.stringify(rightItem)) {
          differences[key].modified.push({
            id: leftItem.id || itemKey,
            old: leftItem,
            new: rightItem,
            changes: findObjectDifferences(leftItem, rightItem)
          });
        }
      });
    };

    compareArrays(left?.gymnasts, right?.gymnasts, 'gymnasts');
    compareArrays(left?.teams, right?.teams, 'teams');
    compareArrays(left?.sessions, right?.sessions, 'sessions');
    compareArrays(left?.judges, right?.judges, 'judges');
    compareArrays(left?.coaches, right?.coaches, 'coaches');

    // Statistics changes
    if (left?.statistics && right?.statistics) {
      Object.keys(right.statistics).forEach(key => {
        if (JSON.stringify(left.statistics[key]) !== JSON.stringify(right.statistics[key])) {
          differences.statistics.push({
            field: key,
            old: left.statistics[key],
            new: right.statistics[key]
          });
        }
      });
    }

    return differences;
  };

  const findObjectDifferences = (oldObj, newObj) => {
    const changes = [];
    const debugChanges = [];
    const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
    const debugFields = ['_debug_original', '_debug', '__debug'];
    
    // Define field priority order
    const fieldPriority = {
      // Session fields (highest priority)
      'sessionNumber': 0,
      'sessionName': 1,
      'description': 2,
      'startDate': 3,
      'levels': 4,
      'timeBlocks': 5,
      
      // Identity fields
      'id': 10,
      'name': 11,
      'firstName': 12,
      'lastName': 13,
      'title': 14,
      
      // Important metadata
      'status': 20,
      'level': 21,
      'ageGroup': 22,
      'gender': 23,
      'date': 24,
      'startTime': 25,
      'endTime': 26,
      
      // Other structural fields
      'events': 30,
      
      // Other fields default to 100
    };
    
    allKeys.forEach(key => {
      if (JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key])) {
        const isDebugField = debugFields.some(debug => key.toLowerCase().includes(debug));
        
        // Separate debug fields from regular fields
        if (isDebugField) {
          if (showDebugFields) {
            debugChanges.push({
              field: key,
              old: oldObj[key],
              new: newObj[key],
              priority: 999
            });
          }
        } else {
          changes.push({
            field: key,
            old: oldObj[key],
            new: newObj[key],
            priority: fieldPriority[key] ?? 100
          });
        }
      }
    });
    
    // Sort changes by priority
    changes.sort((a, b) => a.priority - b.priority);
    
    // Add debug fields at the end with just DEBUG label
    if (debugChanges.length > 0) {
      changes.push(...debugChanges.map(change => ({
        ...change,
        field: 'DEBUG',
        isDebug: true
      })));
    }
    
    return changes;
  };

  const formatValue = (value, isDebugField = false) => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'object') {
      // Format common object types more cleanly
      if (Array.isArray(value)) {
        if (value.length === 0) return '(empty array)';
        // Format arrays nicely
        return JSON.stringify(value, null, 2);
      }
      // For dates
      if (value instanceof Date) {
        return value.toLocaleString();
      }
      // For debug fields, return just the JSON string (we'll handle display separately)
      if (isDebugField) {
        return JSON.stringify(value, null, 2);
      }
      // Pretty print objects
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const highlightDifferences = (oldValue, newValue) => {
    const oldJSON = formatValue(oldValue, true);
    const newJSON = formatValue(newValue, true);
    
    // Parse JSON to compare field by field
    try {
      const oldObj = typeof oldValue === 'object' ? oldValue : JSON.parse(oldJSON);
      const newObj = typeof newValue === 'object' ? newValue : JSON.parse(newJSON);
      
      const allKeys = new Set([...Object.keys(oldObj || {}), ...Object.keys(newObj || {})]);
      const changedKeys = new Set();
      
      allKeys.forEach(key => {
        if (JSON.stringify(oldObj?.[key]) !== JSON.stringify(newObj?.[key])) {
          changedKeys.add(key);
        }
      });
      
      return { oldJSON, newJSON, changedKeys: Array.from(changedKeys) };
    } catch (e) {
      return { oldJSON, newJSON, changedKeys: [] };
    }
  };

  const renderHighlightedJSON = (json, changedKeys) => {
    if (!changedKeys.length) return json;
    
    const lines = json.split('\n');
    return lines.map((line, idx) => {
      const hasChangedKey = changedKeys.some(key => {
        const keyPattern = new RegExp(`"${key}"\\s*:`);
        return keyPattern.test(line);
      });
      
      return (
        <Box
          key={idx}
          component="span"
          sx={{
            display: 'block',
            bgcolor: hasChangedKey ? 'rgba(33, 150, 243, 0.15)' : 'transparent',
            px: hasChangedKey ? 0.5 : 0,
            borderRadius: hasChangedKey ? 0.5 : 0
          }}
        >
          {line}
        </Box>
      );
    });
  };

  const toggleDebugExpanded = (id) => {
    setDebugExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const ensureDebugExpanded = (id) => {
    setDebugExpanded(prev => ({
      ...prev,
      [id]: true
    }));
  };

  useEffect(() => {
    // Initialize all debug sections as expanded by default
    if (diff) {
      const initialExpanded = {};
      
      // Competition metadata debug fields
      if (diff.competition) {
        diff.competition.forEach((change, idx) => {
          if (change.isDebug) {
            initialExpanded[`comp-${idx}`] = true;
          }
        });
      }
      
      // Modified items debug fields for all entity types
      ['gymnasts', 'teams', 'sessions', 'judges', 'coaches'].forEach(key => {
        if (diff[key]?.modified) {
          diff[key].modified.forEach((item, idx) => {
            item.changes.forEach((change, changeIdx) => {
              if (change.isDebug) {
                initialExpanded[`${key}-${idx}-${changeIdx}`] = true;
              }
            });
          });
        }
      });
      
      setDebugExpanded(initialExpanded);
    }
  }, [diff]);

  const formatItemName = (item) => {
    // Session-specific naming
    if (item.sessionName) return item.sessionName;
    if (item.sessionNumber) return `Session ${item.sessionNumber}`;
    
    // People names (gymnasts, judges, coaches)
    if (item.firstName && item.lastName) return `${item.firstName} ${item.lastName}`;
    
    // Generic names
    if (item.name) return item.name;
    if (item.title) return item.title;
    if (item.label) return item.label;
    
    // Fallback
    if (item.id) return `ID: ${item.id}`;
    return '';
  };

  const formatItemPreview = (item) => {
    // Prioritize meaningful fields (most useful first)
    const priorityFields = ['level', 'ageGroup', 'session', 'flight', 'squad', 'gender', 'age', 'dateOfBirth', 'status', 'teamId', 'usagId', 'clubName', 'type', 'role'];
    const excludeFields = ['id', 'created_at', 'updated_at', 'firstName', 'lastName', 'name', 'title', 'events'];
    
    // Debug: log first item to see what fields exist
    if (Math.random() < 0.01) { // Only log 1% to avoid console spam
      // console.log('Item fields:', Object.keys(item));
      // console.log('Item data:', item);
    }
    
    const preview = [];
    
    // Add priority fields in order if they exist
    for (const field of priorityFields) {
      if (item[field] !== undefined && item[field] !== null && item[field] !== '') {
        const val = item[field];
        const displayVal = typeof val === 'object' ? JSON.stringify(val) : val;
        preview.push(`${field}: ${displayVal}`);
        if (preview.length >= 3) break;
      }
    }
    
    return preview.length > 0 ? preview.join(' • ') : 'No additional details';
  };

  const renderDiffSummary = () => {
    if (!diff) return null;

    const totalChanges = 
      diff.competition.length +
      diff.gymnasts.added.length + diff.gymnasts.removed.length + diff.gymnasts.modified.length +
      diff.teams.added.length + diff.teams.removed.length + diff.teams.modified.length +
      diff.sessions.added.length + diff.sessions.removed.length + diff.sessions.modified.length +
      diff.judges.added.length + diff.judges.removed.length + diff.judges.modified.length +
      diff.coaches.added.length + diff.coaches.removed.length + diff.coaches.modified.length +
      diff.statistics.length;

    if (totalChanges === 0) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          No differences found between these revisions
        </Alert>
      );
    }

    return null;
  };

  const renderCompetitionChanges = () => {
    if (!diff?.competition?.length) return null;

    return (
      <Accordion defaultExpanded TransitionProps={{ timeout: 200 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Competition Metadata ({diff.competition.length} changes)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List>
            {diff.competition.map((change, idx) => (
              <ListItem key={idx} divider>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {change.field === 'sessionNumber' ? 'SESSION #' : change.field}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        borderLeft: 3, 
                        borderColor: 'error.main',
                        bgcolor: 'background.default'
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Before (Rev {leftRevision})
                      </Typography>
                      {change.isDebug ? (
                        <Box>
                          <Box
                            onClick={() => toggleDebugExpanded(`comp-${idx}`)}
                            sx={{
                              cursor: 'pointer',
                              userSelect: 'none',
                              color: 'primary.main',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              mt: 1,
                              mb: 1
                            }}
                          >
                            {debugExpanded[`comp-${idx}`] ? '▼' : '▶'} Click to Expand
                          </Box>
                          {debugExpanded[`comp-${idx}`] && (
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.900', borderRadius: 1, overflow: 'auto', maxHeight: 400 }}>
                              <Typography component="pre" variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', margin: 0, color: 'grey.100' }}>
                                {(() => {
                                  const { oldJSON, changedKeys } = highlightDifferences(change.old, change.new);
                                  return renderHighlightedJSON(oldJSON, changedKeys);
                                })()}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                          {formatValue(change.old)}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        borderLeft: 3, 
                        borderColor: 'success.main',
                        bgcolor: 'background.default'
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        After (Rev {rightRevision})
                      </Typography>
                      {change.isDebug ? (
                        <Box>
                          <Box
                            onClick={() => toggleDebugExpanded(`comp-${idx}`)}
                            sx={{
                              cursor: 'pointer',
                              userSelect: 'none',
                              color: 'primary.main',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              mt: 1,
                              mb: 1
                            }}
                          >
                            {debugExpanded[`comp-${idx}`] ? '▼' : '▶'} Click to Expand
                          </Box>
                          {debugExpanded[`comp-${idx}`] && (
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.900', borderRadius: 1, overflow: 'auto', maxHeight: 400 }}>
                              <Typography component="pre" variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', margin: 0, color: 'grey.100' }}>
                                {(() => {
                                  const { newJSON, changedKeys } = highlightDifferences(change.old, change.new);
                                  return renderHighlightedJSON(newJSON, changedKeys);
                                })()}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                          {formatValue(change.new)}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderArrayChanges = (key, label) => {
    const changes = diff?.[key];
    if (!changes) return null;

    const totalChanges = changes.added.length + changes.removed.length + changes.modified.length;
    if (totalChanges === 0) return null;

    const parts = [];
    if (changes.added.length > 0) parts.push(`${changes.added.length} added`);
    if (changes.removed.length > 0) parts.push(`${changes.removed.length} removed`);
    if (changes.modified.length > 0) parts.push(`${changes.modified.length} modified`);
    const summary = parts.join(', ');

    return (
      <Accordion 
        expanded={accordionExpanded[key] !== false}
        onChange={(e, isExpanded) => setAccordionExpanded(prev => ({ ...prev, [key]: isExpanded }))}
        TransitionProps={{ timeout: 200 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
            <Typography variant="h6">
              {label} ({summary})
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
              {changes.added.length > 0 && (
                <Chip 
                  icon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                  label={changes.added.length}
                  size="small"
                  color="success"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAccordionExpanded(prev => ({ ...prev, [key]: true }));
                    setTimeout(() => {
                      document.getElementById(`${key}-added`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }, 250);
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              )}
              {changes.removed.length > 0 && (
                <Chip 
                  icon={<RemoveIcon sx={{ fontSize: '16px !important' }} />}
                  label={changes.removed.length}
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAccordionExpanded(prev => ({ ...prev, [key]: true }));
                    setTimeout(() => {
                      document.getElementById(`${key}-removed`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }, 250);
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              )}
              {changes.modified.length > 0 && (
                <Chip 
                  icon={<FiberManualRecordIcon sx={{ fontSize: '12px !important' }} />}
                  label={changes.modified.length}
                  size="small"
                  color="warning"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Expand accordion
                    setAccordionExpanded(prev => ({ ...prev, [key]: true }));
                    // Expand all debug sections in modified items before scrolling
                    changes.modified.forEach((item, idx) => {
                      item.changes.forEach((change, changeIdx) => {
                        if (change.isDebug) {
                          ensureDebugExpanded(`${key}-${idx}-${changeIdx}`);
                        }
                      });
                    });
                    setTimeout(() => {
                      document.getElementById(`${key}-modified`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }, 250);
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* Added items */}
          {changes.added.length > 0 && (
            <Box id={`${key}-added`} sx={{ mb: 2, scrollMarginTop: '100px' }}>
              <Typography variant="subtitle1" color="success.main" gutterBottom>
                <AddIcon fontSize="small" sx={{ verticalAlign: 'middle' }} /> Added ({changes.added.length})
              </Typography>
              <List dense>
                {changes.added.map((item, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mb: 1,
                      borderLeft: 3,
                      borderColor: 'success.main',
                      bgcolor: 'background.default'
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {formatItemName(item)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {formatItemPreview(item)}
                    </Typography>
                  </Paper>
                ))}
              </List>
            </Box>
          )}

          {/* Removed items */}
          {changes.removed.length > 0 && (
            <Box id={`${key}-removed`} sx={{ mb: 2, scrollMarginTop: '100px' }}>
              <Typography variant="subtitle1" color="error.main" gutterBottom>
                <RemoveIcon fontSize="small" sx={{ verticalAlign: 'middle' }} /> Removed ({changes.removed.length})
              </Typography>
              <List dense>
                {changes.removed.map((item, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mb: 1,
                      borderLeft: 3,
                      borderColor: 'error.main',
                      bgcolor: 'background.default'
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {formatItemName(item)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {formatItemPreview(item)}
                    </Typography>
                  </Paper>
                ))}
              </List>
            </Box>
          )}

          {/* Modified items */}
          {changes.modified.length > 0 && (
            <Box id={`${key}-modified`} sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="subtitle1" color="warning.main" gutterBottom>
                <FiberManualRecordIcon fontSize="small" sx={{ verticalAlign: 'middle' }} /> Modified ({changes.modified.length})
              </Typography>
              {changes.modified.map((item, idx) => (
                <Card key={idx} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      {formatItemName(item.new)}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    {item.changes.map((change, changeIdx) => (
                      <Box key={changeIdx} sx={{ mb: 2 }}>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            textTransform: 'uppercase', 
                            fontWeight: 600
                          }}
                        >
                          {change.field === 'sessionNumber' ? 'SESSION #' : change.field}
                        </Typography>
                        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                          <Grid item xs={6}>
                            <Paper 
                              variant="outlined" 
                              sx={{
                                p: 1.5,
                                borderLeft: 2,
                                borderColor: 'error.main',
                                bgcolor: 'background.default'
                              }}
                            >
                              {change.isDebug ? (
                                <Box>
                                  <Box
                                    onClick={() => toggleDebugExpanded(`${key}-${idx}-${changeIdx}`)}
                                    sx={{
                                      cursor: 'pointer',
                                      userSelect: 'none',
                                      color: 'primary.main',
                                      fontSize: '0.875rem',
                                      fontWeight: 500,
                                      mb: 1
                                    }}
                                  >
                                    {debugExpanded[`${key}-${idx}-${changeIdx}`] ? '▼' : '▶'} Click to Expand
                                  </Box>
                                  {debugExpanded[`${key}-${idx}-${changeIdx}`] && (
                                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.900', borderRadius: 1, overflow: 'auto', maxHeight: 400 }}>
                                      <Typography component="pre" variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', margin: 0, color: 'grey.100' }}>
                                        {(() => {
                                          const { oldJSON, changedKeys } = highlightDifferences(change.old, change.new);
                                          return renderHighlightedJSON(oldJSON, changedKeys);
                                        })()}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              ) : typeof change.old === 'object' && change.old !== null ? (
                                <Box>
                                  <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.900', borderRadius: 1, overflow: 'auto', maxHeight: 400 }}>
                                    <Typography component="pre" variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', margin: 0, color: 'grey.100' }}>
                                      {(() => {
                                        const { oldJSON, changedKeys } = highlightDifferences(change.old, change.new);
                                        return renderHighlightedJSON(oldJSON, changedKeys);
                                      })()}
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : (
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', minHeight: '24px' }}>
                                  {formatValue(change.old) || '\u00A0'}
                                </Typography>
                              )}
                            </Paper>
                          </Grid>
                          <Grid item xs={6}>
                            <Paper 
                              variant="outlined" 
                              sx={{
                                p: 1.5,
                                borderLeft: 2,
                                borderColor: 'success.main',
                                bgcolor: 'background.default'
                              }}
                            >
                              {change.isDebug ? (
                                <Box>
                                  <Box
                                    onClick={() => toggleDebugExpanded(`${key}-${idx}-${changeIdx}`)}
                                    sx={{
                                      cursor: 'pointer',
                                      userSelect: 'none',
                                      color: 'primary.main',
                                      fontSize: '0.875rem',
                                      fontWeight: 500,
                                      mb: 1
                                    }}
                                  >
                                    {debugExpanded[`${key}-${idx}-${changeIdx}`] ? '▼' : '▶'} Click to Expand
                                  </Box>
                                  {debugExpanded[`${key}-${idx}-${changeIdx}`] && (
                                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.900', borderRadius: 1, overflow: 'auto', maxHeight: 400 }}>
                                      <Typography component="pre" variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', margin: 0, color: 'grey.100' }}>
                                        {(() => {
                                          const { newJSON, changedKeys } = highlightDifferences(change.old, change.new);
                                          return renderHighlightedJSON(newJSON, changedKeys);
                                        })()}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              ) : typeof change.new === 'object' && change.new !== null ? (
                                <Box>
                                  <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.900', borderRadius: 1, overflow: 'auto', maxHeight: 400 }}>
                                    <Typography component="pre" variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', margin: 0, color: 'grey.100' }}>
                                      {(() => {
                                        const { newJSON, changedKeys } = highlightDifferences(change.old, change.new);
                                        return renderHighlightedJSON(newJSON, changedKeys);
                                      })()}
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : (
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', minHeight: '24px' }}>
                                  {formatValue(change.new) || '\u00A0'}
                                </Typography>
                              )}
                            </Paper>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderStatisticsChanges = () => {
    if (!diff?.statistics?.length) return null;

    return (
      <Accordion TransitionProps={{ timeout: 200 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Statistics ({diff.statistics.length} changes)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List>
            {diff.statistics.map((change, idx) => (
              <ListItem key={idx} divider>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {change.field}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 1.5, 
                        borderLeft: 2, 
                        borderColor: 'error.main',
                        bgcolor: 'background.default'
                      }}
                    >
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {formatValue(change.old)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 1.5, 
                        borderLeft: 2, 
                        borderColor: 'success.main',
                        bgcolor: 'background.default'
                      }}
                    >
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {formatValue(change.new)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <CompareArrowsIcon />
            <Typography variant="h6">Compare Revisions</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              size="small"
              variant={showDebugFields ? "contained" : "outlined"}
              onClick={() => setShowDebugFields(!showDebugFields)}
              sx={{ textTransform: 'none' }}
            >
              {showDebugFields ? 'Hide Debug' : 'Show Debug'}
            </Button>
            <IconButton edge="end" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent ref={contentRef} dividers sx={{ pt: 3, position: 'relative' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error && !diff ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {/* Revision selectors */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
              <TextField
                select
                fullWidth
                label="Previous Version"
                value={leftRevision}
                onChange={(e) => setLeftRevision(e.target.value)}
              >
                {versions.map((v) => (
                  <MenuItem key={v.revision} value={v.revision}>
                    Version {v.revision} - {new Date(v.created_at + 'Z').toLocaleString()}
                  </MenuItem>
                ))}
              </TextField>

              <Box 
                sx={{ 
                  flexShrink: 0, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  minWidth: 40,
                  transition: 'all 0.3s ease-in-out'
                }}
              >
                {comparing ? (
                  <CircularProgress size={24} sx={{ opacity: 1, transition: 'opacity 0.2s' }} />
                ) : (
                  <CompareArrowsIcon sx={{ opacity: 0.6, transition: 'opacity 0.2s' }} />
                )}
              </Box>

              <TextField
                select
                fullWidth
                label="Current Version"
                value={rightRevision}
                onChange={(e) => setRightRevision(e.target.value)}
              >
                {versions.map((v) => (
                  <MenuItem key={v.revision} value={v.revision}>
                    Version {v.revision} - {new Date(v.created_at + 'Z').toLocaleString()}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {leftRevision === rightRevision && leftRevision && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Please select two different revisions to compare
              </Alert>
            )}

            {/* Comparison results */}
            {diff && (
              <Box>
                {renderDiffSummary()}
                {renderCompetitionChanges()}
                {renderArrayChanges('gymnasts', 'Gymnasts')}
                {renderArrayChanges('teams', 'Teams')}
                {renderArrayChanges('sessions', 'Sessions')}
                {renderArrayChanges('judges', 'Judges')}
                {renderArrayChanges('coaches', 'Coaches')}
                {renderStatisticsChanges()}
              </Box>
            )}
          </>
        )}
        
        {/* Scroll to top button */}
        {showScrollTop && (
          <Fab
            size="small"
            color="primary"
            onClick={scrollToTop}
            sx={{
              position: 'fixed',
              bottom: 80,
              right: 32,
              zIndex: 1300
            }}
          >
            <KeyboardArrowUpIcon />
          </Fab>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RevisionCompareDialog;
